/**
 * Common schemas used across domains
 */
import { z } from "zod";
/**
 * Strapi ID schema - can be number or string depending on context
 * Strapi 4 uses numeric IDs but they may be serialized as strings in some contexts
 */
export const StrapiIdSchema = z.union([z.number().int().positive(), z.string()]);
/**
 * Currency code schema
 */
export const CurrencyCodeSchema = z.enum(["EUR", "USD", "GBP"]).or(z.string());
/**
 * Referrer type schema
 * Note: Defined here (not in user.schemas.ts) to avoid circular dependencies with relations.ts
 */
export const ReferrerTypeSchema = z.enum(["DEFAULT", "AMBASSADOR", "INFLUENCER", "DISTRIBUTOR", "REQUEST_DISTRIBUTOR"]);
/**
 * Dynamic fee bracket schema - defines commission percentage for profit ranges
 * Note: Defined here to avoid circular dependencies between relations.ts and subscription.schemas.ts
 */
export const DynamicFeeBracketSchema = z.object({
    below: z.number().nullable(), // null means "above all previous brackets"
    fee: z.number(), // Commission rate (e.g., 0.25 = 25%)
});
/**
 * Bot risk level schema
 * Note: Defined here to be shared between relations.ts and bot-config.schemas.ts
 */
export const BotRiskSchema = z.enum(["ultra_low", "low", "medium", "high", "custom"]);
/**
 * Exchange type schema
 * Note: Defined here to avoid circular dependencies between relations.ts and bot-config.schemas.ts
 */
export const ExchangeTypeSchema = z.enum(["crypto", "tradfi"]);
/**
 * Charge risk level schema
 * Note: Defined here to avoid circular dependencies between relations.ts and charge.schemas.ts
 */
export const ChargeRiskLevelSchema = z.enum(["low", "high"]);
/**
 * Invoice status schema - matches Strapi enum
 * Note: Defined here to avoid circular dependencies between relations.ts and invoice.schemas.ts
 */
export const InvoiceStatusSchema = z.enum(["pending", "processing", "paid", "cancelled", "failed"]);
/**
 * Invoice series schema - Spanish tax series codes
 * Note: Defined here to avoid circular dependencies between relations.ts and invoice.schemas.ts
 */
export const InvoiceSeriesSchema = z.enum([
    "F", // Standard invoice
    "AF", // Auto-factura
    "AFP", // Auto-factura pending
    "AFEU", // Auto-factura EU
    "AFEUP", // Auto-factura EU pending
    "AFEX", // Auto-factura non-EU
    "AFEXP", // Auto-factura non-EU pending
    "S", // Simplified
    "EU", // EU invoice
    "OSS", // One-Stop Shop
    "EX", // Export
    "R", // Rectificative
]);
/**
 * Invoice type code schema - Spanish TicketBAI codes
 * Note: Defined here to avoid circular dependencies between relations.ts and invoice.schemas.ts
 */
export const InvoiceTypeCodeSchema = z.enum([
    "F1", // Factura
    "F2", // Factura simplificada
    "F3", // Factura rectificativa
    "R1", // Factura rectificativa por error fundado
    "R2", // Factura rectificativa por Art.80.3
    "R3", // Factura rectificativa por Art.80.4
    "R4", // Factura rectificativa otros
    "R5", // Factura rectificativa en facturas simplificadas
]);
/**
 * Invoice direction schema
 * Note: Defined here to avoid circular dependencies between relations.ts and invoice.schemas.ts
 */
export const InvoiceDirectionSchema = z.enum([
    "OUTGOING", // Normal invoice to customer
    "AUTOFACTURA", // Self-invoice (commission)
]);
/**
 * Invoice type schema
 * Note: Defined here to avoid circular dependencies between relations.ts and invoice.schemas.ts
 */
export const InvoiceTypeSchema = z.enum(["subscription", "one_time", "refund", "payout", "commission"]);
/**
 * Verifactu status schema - Spanish tax system submission status
 * Note: Shared between charge.schemas.ts and invoice.schemas.ts
 */
export const VerifactuStatusSchema = z.enum(["pending", "sent", "confirmed", "failed"]);
/**
 * Payment processor schema
 * Note: Shared between charge.schemas.ts, payment-method.schemas.ts, and relations.ts
 */
export const PaymentProcessorSchema = z.enum(["revolut", "manual"]);
/**
 * Exchange platform schema
 * Note: Shared between bot-config.schemas.ts and relations.ts
 */
export const ExchangePlatformSchema = z.enum(["BINANCE", "METATRADER", "KRAKEN", "FIX"]);
/**
 * Severity schema - used for warnings and notifications
 * Note: Shared between analytics.schemas.ts and relations.ts
 */
export const SeveritySchema = z.enum(["info", "warning", "error", "critical"]);
/**
 * Trade state schema
 * Note: Shared between trade.schemas.ts and relations.ts
 */
export const TradeStateSchema = z.enum(["OPEN", "CLOSED", "PENDING"]);
/**
 * Exchange account status schema
 * Note: Shared between exchange-account.schemas.ts and relations.ts
 */
export const ExchangeAccountStatusSchema = z.enum(["PENDING", "VALID", "INVALID", "VALIDATING"]);
/**
 * Subscription status schema
 * Note: Shared between subscription.schemas.ts and relations.ts
 */
export const SubscriptionStatusSchema = z.enum([
    "active",
    "failed",
    "paused",
    "unpaid",
    "cancelled",
    "failed_escalated",
]);
/**
 * Payment method details schema - card details like last4, brand, expiration
 */
export const PaymentMethodDetailsSchema = z.object({
    last4: z.string().nullish(),
    brand: z.string().nullish(),
    exp_month: z.string().nullish(),
    exp_year: z.string().nullish(),
    cardholder: z.string().nullish(),
});
/**
 * Advanced stop loss config schema - nested within UserConfigSchema
 */
export const AdvancedStopLossSchema = z.object({
    useCustomStopLoss: z.boolean().nullish(),
    customStopLoss: z.number().nullish(),
    stopLossEnabled: z.boolean().nullish(),
});
/**
 * User config schema - matches the nested userConfig JSON structure from controller
 * This is the source of truth format stored in bot-config.userConfig
 */
export const UserConfigSchema = z.object({
    risk: BotRiskSchema.optional(),
    maxActiveBalance: z.number().nullish(),
    customRisk: z.number().nullish(),
    sailingModeThreshold: z.number().nullish(),
    assets: z.array(z.string()).nullish(),
    maxLeverage: z.number().nullish(),
    minLeverage: z.number().nullish(),
    advancedStopLoss: AdvancedStopLossSchema.nullish(),
});
/**
 * Pagination parameters schema (flat format)
 */
export const PaginationParamsSchema = z
    .object({
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().max(100).optional(),
})
    .strict();
/**
 * Nested pagination parameters schema
 * Frontend sends pagination nested under 'pagination' key
 */
export const NestedPaginationParamsSchema = z
    .object({
    pagination: PaginationParamsSchema.optional(),
})
    .strict();
/**
 * Sort parameters schema
 */
export const SortParamsSchema = z
    .object({
    sort: z.array(z.string()).optional(),
})
    .strict();
/**
 * Fields parameters schema
 * Strapi `fields` query parameter to select which non-relation fields to return.
 * - `[]` (empty array) returns only `id`
 * - `["amount", "status"]` returns `id` + specified fields
 * - `undefined` (not provided) returns all fields
 */
export const FieldsParamsSchema = z
    .object({
    fields: z.array(z.string()).optional(),
})
    .strict();
/**
 * Strapi filter operations helper - creates a schema for typical Strapi filter operators
 * Use this to define type-safe filters for entity fields
 *
 * @see https://docs-v4.strapi.io/dev-docs/api/rest/filters-locale-publication
 *
 * @example
 * const BotConfigFilters = z.object({
 *   id: StrapiFilterOps(StrapiIdSchema).optional(),
 *   disabled: StrapiFilterOps(z.boolean()).optional(),
 * }).strict();
 */
export const StrapiFilterOps = (valueSchema) => z
    .object({
    // Equality operators
    /** Equal - exact match */
    $eq: valueSchema.optional(),
    /** Equal (case-insensitive) - exact match ignoring case */
    $eqi: valueSchema.optional(),
    /** Not equal */
    $ne: valueSchema.optional(),
    /** Not equal (case-insensitive) */
    $nei: valueSchema.optional(),
    // Comparison operators
    /** Less than */
    $lt: valueSchema.optional(),
    /** Less than or equal */
    $lte: valueSchema.optional(),
    /** Greater than */
    $gt: valueSchema.optional(),
    /** Greater than or equal */
    $gte: valueSchema.optional(),
    // Array operators
    /** In array - matches any value in the provided array */
    $in: z.array(valueSchema).optional(),
    /** Not in array - excludes values in the provided array */
    $notIn: z.array(valueSchema).optional(),
    // String operators (case-sensitive)
    /** Contains substring */
    $contains: z.string().optional(),
    /** Does not contain substring */
    $notContains: z.string().optional(),
    /** Starts with prefix */
    $startsWith: z.string().optional(),
    /** Ends with suffix */
    $endsWith: z.string().optional(),
    // String operators (case-insensitive)
    /** Contains substring (case-insensitive) */
    $containsi: z.string().optional(),
    /** Does not contain substring (case-insensitive) */
    $notContainsi: z.string().optional(),
    /** Starts with prefix (case-insensitive) */
    $startsWithi: z.string().optional(),
    /** Ends with suffix (case-insensitive) */
    $endsWithi: z.string().optional(),
    // Null operators
    /** Is null - use true to match null values */
    $null: z.boolean().optional(),
    /** Is not null - use true to match non-null values */
    $notNull: z.boolean().optional(),
    // Negation operator
    /** Not - negates a single value (e.g., { $not: 49 } excludes id 49) */
    $not: valueSchema.optional(),
    // Range operator
    /** Between - value must be within range [min, max] inclusive */
    $between: z.tuple([valueSchema, valueSchema]).optional(),
})
    .partial()
    .strict();
/**
 * Strapi logical filter operators - combines multiple filter conditions
 * Use this to build complex filters with AND/OR/NOT logic
 *
 * @see https://docs-v4.strapi.io/dev-docs/api/rest/filters-locale-publication
 *
 * @example
 * // Find active bots OR bots with profit > 1000
 * filters: {
 *   $or: [
 *     { status: { $eq: 'active' } },
 *     { profit: { $gt: 1000 } }
 *   ]
 * }
 *
 * @example
 * // Find bots that are active AND have profit > 0
 * filters: {
 *   $and: [
 *     { status: { $eq: 'active' } },
 *     { profit: { $gt: 0 } }
 *   ]
 * }
 */
export const StrapiLogicalFilterOps = (filterSchema) => z
    .object({
    /** AND - all conditions must match */
    $and: z.array(filterSchema).optional(),
    /** OR - at least one condition must match */
    $or: z.array(filterSchema).optional(),
    /** NOT - negates the filter condition */
    $not: filterSchema.optional(),
})
    .partial();
/**
 * Filter parameters schema - generic filters object
 * Note: Prefer entity-specific filter schemas for type safety
 */
export const FilterParamsSchema = z
    .object({
    filters: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// ============================================================================
// Entity Filter Utilities
// ============================================================================
/**
 * Unwraps optional/nullable wrappers to get base schema for filter operations.
 * Handles nested ZodOptional and ZodNullable wrappers.
 */
export function unwrapSchema(schema) {
    if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
        return unwrapSchema(schema.unwrap());
    }
    return schema;
}
/**
 * Creates a filter schema for relation fields.
 * Supports Strapi relation filter patterns:
 * - `{ id: { $eq: value } }` - filter by relation id with operator
 * - `{ id: value }` - filter by relation id (shorthand)
 * - `{ name: { $eq: "Admin" } }` - filter by any scalar field in the relation
 * - `{ $notNull: true }` - filter by relation existence
 * - `{ $null: true }` - filter by relation non-existence
 *
 * Note: Uses passthrough() to allow filtering by any field in the related entity,
 * since Strapi supports filtering by any nested scalar field.
 */
export function createRelationFilter() {
    return z
        .object({
        id: createFlexibleFilterOps(StrapiIdSchema),
        $null: z.boolean().optional(),
        $notNull: z.boolean().optional(),
    })
        .passthrough()
        .optional();
}
/**
 * Creates a flexible filter schema that accepts both shorthand and explicit operator patterns.
 * - Shorthand: `{ field: value }` (e.g., `{ disabled: false }`)
 * - Explicit: `{ field: { $eq: value } }` (e.g., `{ disabled: { $eq: false } }`)
 */
export function createFlexibleFilterOps(valueSchema) {
    return z.union([valueSchema, StrapiFilterOps(valueSchema)]).optional();
}
/**
 * Creates an entity filter schema by extracting fields from a relation schema.
 * This utility reuses existing field definitions from *RelationSchema to avoid
 * duplicating type definitions for filter fields.
 *
 * Supports both Strapi filter patterns:
 * - Shorthand: `{ field: value }` (e.g., `{ disabled: false }`)
 * - Explicit operators: `{ field: { $eq: value } }` (e.g., `{ disabled: { $eq: false } }`)
 *
 * @param relationSchema - The *RelationSchema containing scalar fields
 * @param config.scalars - Array of scalar field names to make filterable
 * @param config.relations - Array of relation field names (use { id: { $eq } } pattern)
 *
 * @example
 * ```typescript
 * const UserFiltersSchema = createEntityFilterSchema(UserRelationSchema, {
 *   scalars: ["email", "username", "blocked", "isVIP"],
 *   relations: ["referral", "subscription_tier"],
 * });
 * ```
 */
export function createEntityFilterSchema(relationSchema, config) {
    const shape = {};
    // Always include id filter since it's commonly used
    shape["id"] = createFlexibleFilterOps(StrapiIdSchema);
    // Add logical operators for complex filter combinations
    shape["$and"] = z.array(z.record(z.unknown())).optional();
    shape["$or"] = z.array(z.record(z.unknown())).optional();
    shape["$not"] = z.record(z.unknown()).optional();
    // Extract scalar fields from relation schema and wrap with flexible filter ops
    // This allows both shorthand (value) and explicit ({ $eq: value }) patterns
    for (const field of config.scalars) {
        const fieldSchema = relationSchema.shape[field];
        if (fieldSchema) {
            // Unwrap optional/nullable to get base type for filter ops
            const baseSchema = unwrapSchema(fieldSchema);
            shape[field] = createFlexibleFilterOps(baseSchema);
        }
    }
    // Add relation filters with id pattern
    for (const relation of config.relations ?? []) {
        shape[relation] = createRelationFilter();
    }
    // Use passthrough() to allow additional filter fields not explicitly listed
    // This is needed because Strapi allows filtering by any relation field
    return z.object(shape).passthrough();
}
/**
 * Populate parameters schema
 */
export const PopulateQueryParamsSchema = z
    .object({
    populate: z.array(z.string()).optional(),
})
    .strict();
/**
 * Combined query parameters schema (flat pagination)
 */
export const QueryParamsSchema = PaginationParamsSchema.merge(SortParamsSchema).merge(FilterParamsSchema).strict();
/**
 * Combined query parameters schema (nested pagination)
 * Use this for frontend queries that send pagination nested
 * Note: Prefer entity-specific query params schemas (e.g., BotConfigQueryParamsSchema) for type safety
 */
export const NestedQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(FilterParamsSchema)
    .merge(PopulateQueryParamsSchema)
    .extend({
    publicationState: z.enum(["live", "preview"]).optional(),
})
    .strict();
/**
 * ISO date string schema
 */
export const IsoDateStringSchema = z
    .string()
    .refine((val) => !isNaN(Date.parse(val)), { message: "Invalid date string" });
/**
 * Optional ISO date string schema
 */
export const OptionalIsoDateStringSchema = IsoDateStringSchema.optional().nullable();
/**
 * Timestamp fields schema
 */
export const TimestampFieldsSchema = z.object({
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    publishedAt: z.string().nullish(),
});
/**
 * Base Strapi entity schema
 */
export const StrapiEntitySchema = TimestampFieldsSchema.extend({
    id: StrapiIdSchema,
});
/**
 * Pagination metadata schema
 */
export const PaginationMetaSchema = z.object({
    page: z.number(),
    pageSize: z.number(),
    pageCount: z.number(),
    total: z.number(),
});
/**
 * Response metadata schema
 */
export const ResponseMetaSchema = z.object({
    pagination: PaginationMetaSchema.optional(),
});
/**
 * Strapi media format schema
 */
export const StrapiMediaFormatSchema = z.object({
    ext: z.string(),
    url: z.string(),
    hash: z.string(),
    mime: z.string(),
    name: z.string(),
    size: z.number(),
    width: z.number(),
    height: z.number(),
});
/**
 * Strapi media schema
 */
export const StrapiMediaSchema = z.object({
    id: StrapiIdSchema,
    name: z.string(),
    alternativeText: z.string().nullish(),
    caption: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    formats: z.record(StrapiMediaFormatSchema).nullish(),
    hash: z.string(),
    ext: z.string(),
    mime: z.string(),
    size: z.number(),
    url: z.string(),
    previewUrl: z.string().nullish(),
    provider: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Localization schema
 */
export const LocalizationSchema = z.object({
    id: StrapiIdSchema,
    locale: z.string(),
});
/**
 * Populate params schema
 */
export const PopulateParamsSchema = z
    .object({
    populate: z.union([z.array(z.string()), z.string(), z.literal("*")]).optional(),
})
    .strict();
/**
 * Publication state schema
 */
export const PublicationStateSchema = z.enum(["live", "preview"]);
/**
 * API Error class with status code
 */
export class ApiError extends Error {
    constructor(message, status = 500, code, details) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.code = code;
        this.details = details;
        // Maintain proper stack trace for where our error was thrown
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
    }
    get isUnauthorized() {
        return this.status === 401;
    }
    get isForbidden() {
        return this.status === 403;
    }
    get isNotFound() {
        return this.status === 404;
    }
    get isValidationError() {
        return this.status === 400 || this.status === 422;
    }
    get isServerError() {
        return this.status >= 500;
    }
}
